// src/pages/SalesVoucher/SalesQuickVoucher.jsx
import React, { useEffect, useRef, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { App, Button, Card, Col, Divider, Row, Spin, Typography } from "antd";
import { ArrowLeftOutlined, PrinterOutlined } from "@ant-design/icons";
import { useReactToPrint } from "react-to-print";

import ReportDom from "../../../../components/common/ReportDom";
import { getInstitutionApi } from "../../../../apiservice/setting";
import { salesRecordWithDetailsApi } from "../../../../apiservice/sales";

// Reuse your existing bodies
import SalesVoucherBody from "../../../SalesVoucher/components/SalesVoucherBody";
import SalesInsuranceVoucher from "../../../SalesVoucher/components/SalesInsuranceVoucher";
import CorporateVoucher from "../../../SalesVoucher/components/CorporateVoucher";
import { useShareDocument } from "../../../../hooks/useShareDocument";
import ShareDocumentButton from "../../../../components/ShareButton/ShareDocumentButton";

const { Title } = Typography;

const SalesPharmaQuickVoucher = () => {
  const { id } = useParams(); // route: /sales/quick-sales-voucher/:id
  const navigate = useNavigate();
  const { message } = App.useApp();

  const reportRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [institution, setInstitution] = useState(null);
  const [voucher, setVoucher] = useState(null);
  const [customerEmail, setCustomerEmail] = useState("");

  // Decide which voucher body to render (Insurance / Corporate / Normal)
  const BodyComponent = useMemo(() => {
    if (!voucher) return null;

    // // 1) Insurance
    if (voucher?.insurance_id) return "insurance";

    // 2) Corporate (mimic old UI logic)
    // Old UI: authInfo.branch_company_type === "Information Technology"
    // New UI: derive from institution if available
    const isCorporate =
      (institution?.branch_company_type || "").toLowerCase() ===
      "information technology";

    if (isCorporate) return "corporate";

    // 3) Normal
    return "normal";
  }, [voucher, institution]);

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: `Sales_Voucher_${voucher?.sale_voucher_no || "Voucher"}`,
    removeAfterPrint: true,
  });

  const {
      shareOpen,
      setShareOpen,
      email,
      setEmail,
      handleEmailShare,
      handleWhatsAppShare,
    } = useShareDocument();

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);

        const [instRes, voucherRes] = await Promise.all([
          getInstitutionApi(),
          salesRecordWithDetailsApi({ sale_id: id, from: "voucher" }),
        ]);

        setInstitution(instRes?.data || null);

        if (Array.isArray(voucherRes?.data) && voucherRes.data.length > 0) {
          setVoucher(voucherRes.data[0]);
        } else {
          setVoucher(null);
          message.warning("No sales voucher found.");
        }
      } catch (err) {
        console.error("Quick Sales Voucher load failed:", err);
        message.error("Failed to load sales voucher.");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchAll();
  }, [id, message]);

  useEffect(() => {
      if (shareOpen && customerEmail ) {
        setEmail(customerEmail);
      }
    }, [shareOpen, customerEmail, email, setEmail]);

  if (loading)
    return (
      <div style={{ textAlign: "center", paddingTop: 100 }}>
        <Spin size="large" />
      </div>
    );

  if (!voucher)
    return (
      <div style={{ padding: 24 }}>
        <Title level={4}>No Voucher Found</Title>
        <Button onClick={() => navigate("/sales-dashboard")}>Back</Button>
      </div>
    );

    const hasBonusItems = voucher?.details?.some(
      (item) => Number(item.item_rate) === 0
    );


  const renderLiveBody = () => {
    if (BodyComponent === "insurance") {
      return (
        <>
        <SalesInsuranceVoucher
          key="insurance-invoice-with-bonus"
          sale_id={voucher.sale_id}
          institution={institution}
          printMode={false}
          setCustomerEmail={setCustomerEmail}
        />
        {hasBonusItems && (
      <>
        <Divider style={{ margin: "48px 0" }}>
          Invoice (Excluding Bonus Items)
        </Divider>
        <SalesInsuranceVoucher
          key="insurance-invoice-without-bonus"
          sale_id={voucher.sale_id}
          institution={institution}
          printMode={false}
          hideBonusItems
          setCustomerEmail={setCustomerEmail}
        />
      </>
      )}
        </>
      );
    }
    if (BodyComponent === "corporate") {
      return (
        <CorporateVoucher
          sale_id={voucher.sale_id}
          institution={institution}
          printMode={false}
          setCustomerEmail={setCustomerEmail}
        />
      );
    }
    return (
      <>
    <SalesVoucherBody key="invoice-with-bonus" sale_id={voucher.sale_id} printMode={false} setCustomerEmail={setCustomerEmail} />
    {hasBonusItems && (
      <>
        <Divider style={{ margin: "48px 0" }}>
          Invoice (Excluding Bonus Items)
        </Divider>
        <SalesVoucherBody
          key="invoice-without-bonus"
          sale_id={voucher.sale_id}                        
          printMode={false}
          hideBonusItems
          setCustomerEmail={setCustomerEmail}
        />
      </>
      )}
    </>
    );
  };

  const renderPrintBody = () => {
    if (BodyComponent === "insurance") {
      return (
        <>
        <SalesInsuranceVoucher
          key="print-insurance-with-bonus"
          sale_id={voucher.sale_id}
          institution={institution}
          printMode
        />
        {hasBonusItems && (
      <>
      <div style={{ pageBreakBefore: "always" }} />
        <SalesInsuranceVoucher
          key="print-insurance-without-bonus"
          sale_id={voucher.sale_id}                        
          printMode
          institution={institution}
          hideBonusItems
        />
      </>
      )}
      </>
      );
    }
    if (BodyComponent === "corporate") {
      return (
        <CorporateVoucher
          sale_id={voucher.sale_id}
          institution={institution}
          printMode
        />
      );
    }
    return (
      <>
    <SalesVoucherBody key="print-with-bonus" sale_id={voucher.sale_id} printMode />
    {hasBonusItems && (
      <>
      <div style={{ pageBreakBefore: "always" }} />
        <SalesVoucherBody
          key="print-without-bonus"
          sale_id={voucher.sale_id}                        
          printMode
          hideBonusItems
        />
      </>
      )}
      </>
  );
  };

  return (
    <div style={{ padding: 24, background: "#f5f5f5", minHeight: "100vh" }}>
      {/* Header Bar */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            Back
          </Button>
        </Col>
        <Col>
            <ShareDocumentButton
              disabled={!voucher}
              shareOpen={shareOpen}
              setShareOpen={setShareOpen}
              email={email}
              setEmail={setEmail}
              loading={loading}
              onEmail={() =>
                handleEmailShare({
                  url: `/print/sales/${voucher.sale_id}`,
                  fileName: `Invoice_${voucher.sale_voucher_no}.pdf`,
                  name: voucher.acc_name,
                  docNo: voucher.sale_voucher_no,
                })
              }
              onWhatsApp={() =>
                handleWhatsAppShare({
                  url: `/print/sales/${voucher.sale_id}`,
                  fileName: `Invoice_${voucher.sale_voucher_no}.pdf`,
                  text: `Hello ${voucher.acc_name}, Your Invoice ${voucher.sale_voucher_no} is ready. Download here:`,
                })
              }
            />
          </Col>

          <Col>
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              onClick={handlePrint}
            >
              Print Voucher
            </Button>
          </Col>
      </Row>

      {/* Voucher Body */}
      <Card className="shadow-md rounded-xl" style={{ padding: 24, background: "white" }}>
        {renderLiveBody()}
      </Card>

      {/* Hidden Printable DOM */}
      <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
        <ReportDom
          ref={reportRef}
          // title="Sales Voucher"
          // filters={{
          //   fromDate: voucher.created_date,
          //   toDate: voucher.created_date,
          // }}
          renderTable={renderPrintBody}
        />
      </div>
    </div>
  );
};

export default SalesPharmaQuickVoucher;
